import React from 'react';
import {
    List,
    SelectInput,
    TextField,
    TextInput,
    useRecordContext,
    useDataProvider,
    useNotify,
    useRefresh,
    useListContext,
    RecordContextProvider,
} from 'react-admin';
import { Box, Button, Chip, CircularProgress, Paper, Typography } from '@mui/material';
import CardGiftcardRoundedIcon from '@mui/icons-material/CardGiftcardRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import { QuestSectionTabs } from './components/QuestSectionTabs';

const STATUS_CHOICES = [
    { id: 'purchased', name: 'Куплено (ожидает выдачи)' },
    { id: 'issued', name: 'Выдано' },
    { id: 'cancelled', name: 'Отменено' },
];

const STATUS_LABEL = {
    purchased: { label: 'Ожидает выдачи', color: 'warning' },
    issued: { label: 'Выдано', color: 'success' },
    cancelled: { label: 'Отменено', color: 'default' },
};

// Фильтры: поиск покупок конкретного пользователя (по его user_id) и по статусу.
const purchaseFilters = [
    <TextInput source="user_id" label="ID пользователя" alwaysOn resettable />,
    <SelectInput source="status" label="Статус" choices={STATUS_CHOICES} alwaysOn />,
];

// Кому выдавать: имя и телефон подмешаны из userservice, id — на случай,
// когда гость ещё не заполнил профиль и имени нет.
const GuestField = () => {
    const record = useRecordContext();
    if (!record) return null;
    return (
        <Box>
            <Typography variant="body2" fontWeight={600}>
                {record.user_name || 'Имя не указано'}
            </Typography>
            <Typography variant="body2">{record.user_phone || '—'}</Typography>
            <Typography variant="caption" color="text.secondary">
                {record.user_id}
            </Typography>
        </Box>
    );
};

const StatusChip = () => {
    const record = useRecordContext();
    if (!record) return null;
    const meta = STATUS_LABEL[record.status] || { label: record.status, color: 'default' };
    return <Chip size="small" label={meta.label} color={meta.color} />;
};

// Кнопка подтверждения выдачи. Доступна только для статуса "purchased" —
// повторная выдача заблокирована на бэке (purchased -> issued один раз).
const IssueButton = () => {
    const record = useRecordContext();
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const refresh = useRefresh();
    const [loading, setLoading] = React.useState(false);

    if (!record || record.status !== 'purchased') return null;

    const handleIssue = async (e) => {
        e.stopPropagation();
        if (!window.confirm(`Подтвердить выдачу «${record.item_name}»?`)) return;
        setLoading(true);
        try {
            await dataProvider.create('quest-purchase-issue', { data: { id: record.id } });
            notify('Выдача подтверждена', { type: 'success' });
            refresh();
        } catch (error) {
            notify(error.message || 'Не удалось подтвердить выдачу', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Button variant="contained" size="small" onClick={handleIssue} disabled={loading}>
            Выдать
        </Button>
    );
};

const formatDateTime = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString('ru-RU', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const PurchaseCards = () => {
    const { data = [], isPending } = useListContext();
    if (isPending) return <Box sx={{ py: 8, display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>;
    if (!data.length) return <Paper elevation={0} sx={{ p: 6, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: 3 }}><CardGiftcardRoundedIcon sx={{ fontSize: 48, color: 'text.disabled' }} /><Typography variant="h6" sx={{ mt: 1, fontWeight: 800 }}>Покупок пока нет</Typography><Typography color="text.secondary">Новые заявки на выдачу появятся здесь автоматически.</Typography></Paper>;
    return (
        <Box sx={{ display: 'grid', gap: 1.5 }}>
            {data.map((record) => (
                <RecordContextProvider key={record.id} value={record}>
                    <Paper elevation={0} sx={{ p: { xs: 2, md: 2.5 }, border: '1px solid', borderColor: record.status === 'purchased' ? 'warning.light' : 'divider', borderRadius: 3, opacity: record.status === 'cancelled' ? .65 : 1 }}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(220px, 1.2fr) minmax(180px, 1fr) auto auto' }, gap: 2, alignItems: 'center' }}>
                            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', minWidth: 0 }}>
                                <Box sx={{ width: 46, height: 46, borderRadius: 2.5, bgcolor: 'action.hover', display: 'grid', placeItems: 'center', color: 'primary.main', flexShrink: 0 }}><PersonRoundedIcon /></Box>
                                <GuestField />
                            </Box>
                            <Box sx={{ minWidth: 0 }}><Typography variant="caption" color="text.secondary">Награда</Typography><Typography sx={{ fontWeight: 750 }} noWrap>{record.item_name || 'Без названия'}</Typography><Chip size="small" label={`${record.cost_points || 0} очков`} variant="outlined" sx={{ mt: .75 }} /></Box>
                            <Box><StatusChip /><Box sx={{ display: 'flex', gap: .75, alignItems: 'center', mt: 1 }}><ScheduleRoundedIcon sx={{ fontSize: 16, color: 'text.disabled' }} /><Typography variant="caption" color="text.secondary">{formatDateTime(record.issued_at || record.purchased_at)}</Typography></Box></Box>
                            <Box sx={{ justifySelf: { md: 'end' } }}><IssueButton /></Box>
                        </Box>
                    </Paper>
                </RecordContextProvider>
            ))}
        </Box>
    );
};

export const PurchaseList = () => (
    <>
        <QuestSectionTabs />
        <List
            filters={purchaseFilters}
            sort={{ field: 'purchased_at', order: 'DESC' }}
            exporter={false}
            title="Квесты"
            sx={{ '& .RaList-content': { boxShadow: 'none', bgcolor: 'transparent' } }}
        >
            <Box sx={{ mb: 3 }}><Typography variant="h4" sx={{ fontWeight: 850, letterSpacing: '-.03em', mb: .75 }}>Выдача наград</Typography><Typography color="text.secondary">Очередь покупок: проверьте посетителя и подтвердите фактическую выдачу сувенира.</Typography></Box>
            <PurchaseCards />
        </List>
    </>
);
